import { gql, useLazyQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  ItemResaleRaffleUsersQuery,
  ItemResaleRaffleUsersQueryVariables,
  ResaleConnectionFragmentDoc,
  ResaleManagementQuery,
  ResaleManagementQueryVariables,
  ResaleRaffleConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { ResaleItemStatus, ResaleRaffleStatus } from '@/components/organisms'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const params = new URLSearchParams(window.location.search)
  const eventFilter = params.get('filter') ?? ''
  const { router } = useRouter<{ tab?: 'raffle' | 'purchased' }>()
  const [tab, setTab] = useState<number>(4)

  const options = [
    {
      label: 'exhibiting',
      value: [ResaleItemStatus.Open, ResaleItemStatus.Matched],
    },
    {
      label: 'failedToSell',
      value: [ResaleItemStatus.NoMatched],
    },
    {
      label: 'sold',
      value: [ResaleItemStatus.Sold, ResaleItemStatus.Paid],
    },
    {
      label: 'cancel',
      value: [ResaleItemStatus.Canceled],
    },
    {
      label: 'whole',
      value: [
        ResaleRaffleStatus.Win,
        ResaleRaffleStatus.Applied,
        ResaleRaffleStatus.Lost,
        ResaleRaffleStatus.Invalid,
        ResaleRaffleStatus.Canceled,
      ],
    },
    {
      label: 'waitingPayment',
      value: [ResaleRaffleStatus.Win],
    },
    {
      label: 'waitingLottery',
      value: [ResaleRaffleStatus.Applied],
    },
    {
      label: 'lost',
      value: [ResaleRaffleStatus.Lost],
    },
    {
      label: 'invalid',
      value: [ResaleRaffleStatus.Invalid],
    },
    {
      label: 'revoke',
      value: [ResaleRaffleStatus.Canceled],
    },
  ]
  const matchingValue = options.find(
    option => option.label === eventFilter
  )?.value

  const [getResaleItemsByUser, { data, loading, fetchMore: fetchMoreListed }] =
    useLazyQuery<ResaleManagementQuery, ResaleManagementQueryVariables>(QUERY)

  const [
    getItemResaleRaffleUser,
    {
      data: resaleRaffles,
      loading: resaleRaffleLoading,
      fetchMore: fetchMoreResaleRaffle,
    },
  ] = useLazyQuery<
    ItemResaleRaffleUsersQuery,
    ItemResaleRaffleUsersQueryVariables
  >(ITEM_RESALE_RAFF_USERS)

  useEffect(() => {
    ;(async () => {
      switch (router.query.tab) {
        case 'raffle':
          setTab(1)
          await getItemResaleRaffleUser(
            matchingValue
              ? {
                  variables: {
                    status: matchingValue,
                  },
                }
              : {
                  variables: {
                    status: [
                      ResaleRaffleStatus.Win,
                      ResaleRaffleStatus.Applied,
                      ResaleRaffleStatus.Lost,
                      ResaleRaffleStatus.Invalid,
                      ResaleRaffleStatus.Canceled,
                    ],
                  },
                }
          )
          return
        case 'purchased':
          setTab(2)
          await getItemResaleRaffleUser({
            variables: {
              status: [ResaleRaffleStatus.Purchased],
            },
          })
          return
        default:
          setTab(0)
          await getResaleItemsByUser({
            variables: {
              status: matchingValue,
            },
          })
          return
      }
    })()
  }, [getItemResaleRaffleUser, getResaleItemsByUser, router.query.tab])

  return {
    state: {
      tab,
      loading: loading || resaleRaffleLoading,
      hasNextPage: !!data?.resaleItemsByUser.pageInfo.hasNextPage,
      hasNextPageRaffles:
        !!resaleRaffles?.itemResaleRaffleUsers?.pageInfo.hasNextPage,
      resales: data?.resaleItemsByUser ?? null,
      resaleRaffles: resaleRaffles?.itemResaleRaffleUsers ?? null,
      filter: eventFilter,
    },
    actions: {
      async onFilter(type, status) {
        if (type === 'listed') {
          await getResaleItemsByUser({ variables: { status } })
          return
        }
        if (type === 'raffle') {
          await getItemResaleRaffleUser({
            variables: {
              status: status ?? [
                ResaleRaffleStatus.Win,
                ResaleRaffleStatus.Applied,
                ResaleRaffleStatus.Lost,
                ResaleRaffleStatus.Invalid,
                ResaleRaffleStatus.Canceled,
              ],
            },
          })
          return
        }
        await getItemResaleRaffleUser({
          variables: {
            status: [ResaleRaffleStatus.Purchased],
          },
        })
      },
      async onFetchMore(type, status) {
        if (type === 'listed') {
          await fetchMoreListed({
            variables: {
              status,
              pageInput: { cursor: data?.resaleItemsByUser.pageInfo.endCursor },
            },
          })
          return
        }
        if (type === 'raffle') {
          await fetchMoreResaleRaffle({
            variables: {
              status: status ?? [
                ResaleRaffleStatus.Win,
                ResaleRaffleStatus.Applied,
                ResaleRaffleStatus.Lost,
                ResaleRaffleStatus.Invalid,
              ],
              pageInput: {
                cursor:
                  resaleRaffles?.itemResaleRaffleUsers?.pageInfo.endCursor,
              },
            },
          })
          return
        }
        await fetchMoreResaleRaffle({
          variables: {
            status: [ResaleRaffleStatus.Purchased],
            pageInput: {
              cursor: resaleRaffles?.itemResaleRaffleUsers?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ResaleManagement(
    $pageInput: PageInput
    $status: [String!]
    $skuId: String
  ) {
    resaleItemsByUser(pageInput: $pageInput, status: $status) {
      ...ResaleConnection
    }
  }
  ${ResaleConnectionFragmentDoc}
`

const ITEM_RESALE_RAFF_USERS = gql`
  query ItemResaleRaffleUsers(
    $pageInput: PageInput
    $status: [String!]!
    $skuId: String
  ) {
    itemResaleRaffleUsers(pageInput: $pageInput, status: $status) {
      ...ResaleRaffleConnection
    }
  }
  ${ResaleRaffleConnectionFragmentDoc}
`
